import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, fileUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Check, ArrowRight, ShieldCheck, Download, Printer, Ticket, MapPin, Calendar, Clock, User, Building2 } from "lucide-react";

/* ============================================================
   Public: general registration (/vm/register/:eventSlug)
   ============================================================ */
export function VmRegisterPublic() {
  const { eventSlug } = useParams();
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(baseForm());
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/vm/event/${eventSlug}`);
        setEv(data.event);
      } catch (e) { setError(e?.response?.data?.detail || "Not available"); }
      finally { setLoading(false); }
    })();
  }, [eventSlug]);

  async function submit(e) {
    e.preventDefault();
    if (!form.full_name.trim()) { toast.error("Full name is required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/vm/register/${eventSlug}`, {
        full_name: form.full_name.trim(),
        organisation: form.organisation.trim() || null,
        position: form.position.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        custom_answers: form.custom_answers,
      });
      setResult(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  }

  if (loading) return <PublicShell><div className="text-sm text-gray-500">Loading…</div></PublicShell>;
  if (error || !ev) return <PublicShell><Notice title="Event unavailable" body="This event is not currently accepting registrations." /></PublicShell>;
  if (!ev.public_registration_open) return <PublicShell><Notice title="Registration closed" body="Public registration for this event is currently closed." /></PublicShell>;

  if (result) return (
    <PublicShell>
      <SuccessCard
        title={result.status === "pending" ? "Registration received" : "You're registered!"}
        body={result.message}
        passToken={result.pass_token}
      />
    </PublicShell>
  );

  return (
    <PublicShell>
      <EventHero ev={ev} />
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="vm-public-form">
        <StandardFields form={form} setForm={setForm} />
        {(ev.custom_fields || []).length > 0 && (
          <div className="pt-4 border-t border-gray-100 space-y-4">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Additional details</div>
            {ev.custom_fields.map((f) => (
              <CustomFieldInputPublic key={f.id} field={f}
                value={form.custom_answers[f.id]}
                onChange={(v) => setForm({ ...form, custom_answers: { ...form.custom_answers, [f.id]: v } })} />
            ))}
          </div>
        )}
        <button type="submit" disabled={submitting}
          className="w-full brand-gradient text-white rounded-lg py-3.5 text-base font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
          data-testid="vm-public-submit">
          {submitting ? "Submitting…" : "Submit registration"} <ArrowRight size={16} />
        </button>
        {ev.require_approval_for_public && (
          <div className="text-xs text-gray-500 text-center">Your registration will be reviewed. You&apos;ll be issued a ticket once approved.</div>
        )}
      </form>
    </PublicShell>
  );
}

/* ============================================================
   Public: existing-ticket registration (/vm/ticket/:token)
   ============================================================ */
export function VmTicketRegisterPublic() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(baseForm());
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/vm/ticket/${token}`);
        setCtx(data);
      } catch (e) { setError(e?.response?.data?.detail || "Ticket not found"); }
      finally { setLoading(false); }
    })();
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (!form.full_name.trim()) { toast.error("Full name is required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/vm/ticket/${token}/register`, {
        full_name: form.full_name.trim(),
        organisation: form.organisation.trim() || null,
        position: form.position.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        custom_answers: form.custom_answers,
      });
      setResult(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  }

  if (loading) return <PublicShell><div className="text-sm text-gray-500">Loading…</div></PublicShell>;
  if (error || !ctx) return <PublicShell><Notice title="Ticket not found" body={error || "This ticket link is invalid."} /></PublicShell>;

  const ev = ctx.event;
  const ticket = ctx.ticket;

  if (ticket.status !== "available") {
    // Already registered — offer direct pass link
    return (
      <PublicShell>
        <Notice title="Ticket already registered" body="This ticket has already been assigned. If it's yours, use the link below to view your pass." />
        <div className="mt-4 text-center">
          <Link to={`/vm/pass/${token}`} className="inline-flex items-center gap-2 text-sm text-purple-700 underline underline-offset-2">
            View my pass <ArrowRight size={14} />
          </Link>
        </div>
      </PublicShell>
    );
  }

  if (result) return (
    <PublicShell>
      <SuccessCard title="You're registered!" body="Your digital pass is ready." passToken={result.pass_token} />
    </PublicShell>
  );

  return (
    <PublicShell>
      <EventHero ev={ev} />
      <div className="mt-4 bg-purple-50/60 border border-purple-100 rounded-xl px-4 py-3 flex items-center gap-3">
        <Ticket size={18} className="text-purple-700 shrink-0" />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-purple-700 font-bold">Your ticket</div>
          <div className="font-mono text-lg text-gray-900">#{ticket.ticket_number}</div>
        </div>
      </div>
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="vm-ticket-form">
        <StandardFields form={form} setForm={setForm} />
        {(ev.custom_fields || []).length > 0 && (
          <div className="pt-4 border-t border-gray-100 space-y-4">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Additional details</div>
            {ev.custom_fields.map((f) => (
              <CustomFieldInputPublic key={f.id} field={f}
                value={form.custom_answers[f.id]}
                onChange={(v) => setForm({ ...form, custom_answers: { ...form.custom_answers, [f.id]: v } })} />
            ))}
          </div>
        )}
        <button type="submit" disabled={submitting}
          className="w-full brand-gradient text-white rounded-lg py-3.5 text-base font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
          data-testid="vm-ticket-submit">
          {submitting ? "Registering…" : "Register my ticket"} <ArrowRight size={16} />
        </button>
      </form>
    </PublicShell>
  );
}

/* ============================================================
   Public: digital pass (/vm/pass/:token) — badge style
   ============================================================ */
export function VmPassPublic() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const passRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/vm/pass/${token}`);
        setCtx(data);
      } catch (e) { setError(e?.response?.data?.detail || "Pass not found"); }
      finally { setLoading(false); }
    })();
  }, [token]);

  async function downloadPng() {
    if (!passRef.current) return;
    try {
      // dynamic import so the vendor code isn't paid for other routes
      const { toPng } = await import("html-to-image");
      const dataUrl = await toPng(passRef.current, { pixelRatio: 2, backgroundColor: "#ffffff" });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `pass-${ctx?.ticket?.ticket_number || "expoflo"}.png`;
      link.click();
    } catch {
      toast.error("Could not generate PNG on this device. Use Print → Save as PDF instead.");
    }
  }
  function print() { window.print(); }

  if (loading) return <PublicShell><div className="text-sm text-gray-500">Loading…</div></PublicShell>;
  if (error || !ctx) return <PublicShell><Notice title="Pass not found" body="This pass link is invalid or the ticket hasn't been registered yet." /></PublicShell>;

  const { attendee, ticket, event: ev } = ctx;
  const qrUrl = `${API}/public/vm/qr/${ticket.token}.png?base=${encodeURIComponent(window.location.origin)}`;

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white flex flex-col items-center py-8 px-4">
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff; } }`}</style>

      <div ref={passRef}
        className="w-full max-w-[420px] rounded-3xl overflow-hidden shadow-lg border border-gray-200 bg-white"
        data-testid="vm-pass-card">
        {/* Branded header band */}
        <div className="relative brand-gradient p-6 text-white">
          <div className="absolute inset-0 opacity-20 pointer-events-none"
               style={{ backgroundImage: "radial-gradient(circle at 20% 10%, rgba(255,255,255,0.35), transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.25), transparent 45%)" }} />
          <div className="relative flex items-center gap-3">
            {ev.logo_path ? (
              <img src={fileUrl(ev.logo_path)} alt={ev.name} className="h-10 max-w-[140px] object-contain bg-white/95 rounded-md p-1.5" />
            ) : (
              <div className="bg-white/90 rounded-md px-2 py-1"><Logo className="h-6" /></div>
            )}
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-90">Attendee Pass</div>
          </div>
          <div className="relative mt-6">
            <div className="font-display text-2xl font-semibold leading-tight">{ev.name}</div>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[12px] opacity-90">
              {ev.event_date && <span className="inline-flex items-center gap-1"><Calendar size={12} /> {ev.event_date}</span>}
              {(ev.start_time || ev.end_time) && <span className="inline-flex items-center gap-1"><Clock size={12} /> {[ev.start_time, ev.end_time].filter(Boolean).join(" – ")}</span>}
              {ev.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {ev.location}</span>}
            </div>
          </div>
        </div>

        {/* Attendee body */}
        <div className="p-6 pt-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold">Attendee</div>
          <div className="font-display text-2xl font-semibold text-gray-900 mt-1">{attendee.full_name}</div>
          {(attendee.organisation || attendee.position) && (
            <div className="text-sm text-gray-600 mt-1">
              {[attendee.position, attendee.organisation].filter(Boolean).join(" · ")}
            </div>
          )}

          <div className="mt-5 flex items-center gap-4">
            <img src={qrUrl} alt="Ticket QR" className="w-32 h-32 rounded-md border border-gray-200 bg-white p-1" data-testid="vm-pass-qr" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold">Ticket</div>
              <div className="font-mono text-2xl font-semibold text-gray-900 mt-1" data-testid="vm-pass-ticket-number">#{ticket.ticket_number}</div>
              <div className={`mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium border ${
                ticket.status === "checked_in" ? "bg-emerald-50 text-emerald-800 border-emerald-100" :
                "bg-purple-50 text-purple-800 border-purple-100"
              }`}>{ticket.status === "checked_in" ? "Checked in" : "Registered"}</div>
            </div>
          </div>
        </div>

        {/* Perforation + footer */}
        <div className="relative">
          <div className="border-t border-dashed border-gray-300" />
          <div className="absolute -left-2 -top-2 w-4 h-4 rounded-full bg-gray-50" />
          <div className="absolute -right-2 -top-2 w-4 h-4 rounded-full bg-gray-50" />
        </div>
        <div className="px-6 py-4 flex items-center justify-between text-[11px] text-gray-500">
          <span className="inline-flex items-center gap-1"><ShieldCheck size={12} className="text-purple-600" /> Powered by ExpoFlo AI</span>
          <span className="font-mono">{ticket.token.slice(0, 8)}</span>
        </div>
      </div>

      <div className="no-print mt-6 flex gap-2">
        <button onClick={downloadPng}
          className="inline-flex items-center gap-2 text-sm bg-gray-900 text-white rounded-md px-4 py-2.5 hover:bg-gray-800"
          data-testid="vm-pass-download">
          <Download size={14} /> Download PNG
        </button>
        <button onClick={print}
          className="inline-flex items-center gap-2 text-sm border border-gray-200 rounded-md px-4 py-2.5 hover:bg-gray-50"
          data-testid="vm-pass-print">
          <Printer size={14} /> Print
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   Shared UI
   ============================================================ */
function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-gray-100 h-16 flex items-center px-6">
        <Logo className="h-7" />
        <div className="ml-auto text-xs text-gray-400">Powered by <span className="text-gray-700 font-medium">ExpoFlo AI</span></div>
      </header>
      <main className="flex-1 w-full max-w-xl mx-auto px-6 py-10">
        {children}
      </main>
    </div>
  );
}

function EventHero({ ev }) {
  return (
    <div>
      {ev.logo_path && <img src={fileUrl(ev.logo_path)} alt={ev.name} className="h-12 object-contain mb-4" />}
      <div className="text-[10px] uppercase tracking-widest text-purple-700 font-bold">Event registration</div>
      <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900 mt-1" data-testid="vm-public-event-name">{ev.name}</h1>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
        {ev.event_date && <span className="inline-flex items-center gap-1"><Calendar size={13} /> {ev.event_date}</span>}
        {(ev.start_time || ev.end_time) && <span className="inline-flex items-center gap-1"><Clock size={13} /> {[ev.start_time, ev.end_time].filter(Boolean).join(" – ")}</span>}
        {ev.location && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {ev.location}</span>}
      </div>
      {ev.description && <p className="text-sm text-gray-600 mt-3 leading-relaxed">{ev.description}</p>}
    </div>
  );
}

function StandardFields({ form, setForm }) {
  return (
    <>
      <PublicInput label="Full name *" icon={<User size={14} />} value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required testId="vm-input-fullname" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <PublicInput label="Organisation" icon={<Building2 size={14} />} value={form.organisation} onChange={(v) => setForm({ ...form, organisation: v })} testId="vm-input-org" />
        <PublicInput label="Position" value={form.position} onChange={(v) => setForm({ ...form, position: v })} testId="vm-input-position" />
        <PublicInput label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="vm-input-email" />
        <PublicInput label="Phone" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="vm-input-phone" />
      </div>
    </>
  );
}

function PublicInput({ label, value, onChange, type = "text", required, icon, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500 flex items-center gap-1">{icon}{label}</span>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full border border-gray-200 rounded-lg px-4 py-3 text-base outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
        data-testid={testId} />
    </label>
  );
}

function CustomFieldInputPublic({ field, value, onChange }) {
  if (field.type === "long_text") return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">{field.label}{field.required ? " *" : ""}</span>
      <textarea rows={3} required={field.required} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full border border-gray-200 rounded-lg px-4 py-3 text-base outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 resize-none" />
    </label>
  );
  if (field.type === "single_choice") return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">{field.label}{field.required ? " *" : ""}</span>
      <select value={value || ""} required={field.required} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full border border-gray-200 rounded-lg px-4 py-3 text-base outline-none focus:border-purple-500">
        <option value="">Select…</option>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
  if (field.type === "multi_choice") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500 mb-2">{field.label}{field.required ? " *" : ""}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {field.options.map((o) => {
            const sel = arr.includes(o);
            return (
              <button key={o} type="button"
                onClick={() => onChange(sel ? arr.filter((x) => x !== o) : [...arr, o])}
                className={`text-left text-sm border rounded-lg px-4 py-3 ${sel ? "border-purple-600 bg-purple-50 text-purple-900" : "border-gray-200 hover:border-gray-300"}`}>
                {o}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  return (
    <PublicInput label={`${field.label}${field.required ? " *" : ""}`}
      value={value || ""} onChange={onChange} required={field.required} />
  );
}

function Notice({ title, body }) {
  return (
    <div className="text-center py-6">
      <div className="font-display text-2xl font-semibold text-gray-900">{title}</div>
      <p className="text-sm text-gray-500 mt-2">{body}</p>
    </div>
  );
}

function SuccessCard({ title, body, passToken }) {
  return (
    <div className="text-center py-6" data-testid="vm-public-success">
      <div className="w-16 h-16 rounded-full brand-gradient mx-auto flex items-center justify-center text-white mb-5"><Check size={30} /></div>
      <div className="font-display text-2xl font-semibold text-gray-900">{title}</div>
      <p className="text-sm text-gray-600 mt-2">{body}</p>
      {passToken && (
        <Link to={`/vm/pass/${passToken}`}
          className="mt-6 inline-flex items-center gap-2 brand-gradient text-white rounded-lg px-5 py-3 text-sm font-medium"
          data-testid="vm-public-view-pass">
          View my pass <ArrowRight size={14} />
        </Link>
      )}
    </div>
  );
}

function baseForm() {
  return { full_name: "", organisation: "", position: "", email: "", phone: "", custom_answers: {} };
}
